import { Params } from '@angular/router';
import { Assessment } from './assessment';
import { Group } from './group';
import { ExamStatus } from './exam-status';

export class PageRequest {
  readonly limit: number;
  readonly offset: number;
  readonly order: string;
  readonly sort: string;
  readonly search: string;
  readonly assessments: string[];
  readonly groups: string[];
  readonly groupId: string;
  readonly groupAssessmentId: string;
  readonly status: ExamStatus[];

  constructor(pageRequest?: Partial<PageRequest>) {
    pageRequest = pageRequest ?? {};
    this.limit = pageRequest.limit ?? 10;
    this.offset = pageRequest.offset ?? 0;
    this.search = pageRequest.search ?? '';
    this.order = pageRequest.order ?? undefined;
    this.sort = pageRequest.sort ?? '';
    this.assessments = pageRequest.assessments ?? [];
    this.groups = pageRequest.groups ?? [];
    this.groupId = pageRequest.groupId ?? '';
    this.groupAssessmentId = pageRequest.groupAssessmentId ?? '';
    this.status = pageRequest.status ?? undefined;
  }

  /**
   * Create a PageRequest  corresponding to the given queryParams
   * @returns The PageRequest corresponding to the given queryParams
   */
  static fromParams(queryParams: Params): PageRequest {
    const [order, sort] = (queryParams.sort ?? '').split(',');

    return new PageRequest({
      search: queryParams.search,
      offset: queryParams.offset ? +queryParams.offset : undefined,
      limit: queryParams.limit ? +queryParams.limit : undefined,
      order,
      sort,
    });
  }
  next(): PageRequest {
    return new PageRequest({
      ...this,
      offset: +this.offset + +this.limit,
    });
  }

  previous(): PageRequest {
    return new PageRequest({
      ...this,
      offset: Math.max(this.offset - this.limit, 0),
    });
  }
  setLimit(limit: number): PageRequest {
    return new PageRequest({
      ...this,
      limit: limit,
    });
  }

  setOffset(offset: number): PageRequest {
    return new PageRequest({
      ...this,
      offset: offset,
    });
  }
  withOrder(newOrder: string): PageRequest {
    let { order, sort } = this;
    sort = this.sort.includes('asc') ? 'desc' : 'asc';
    order = newOrder;

    return new Page({ ...this, sort, order });
  }

  addGroupId(groupId: string): PageRequest {
    return new PageRequest({ ...this, groupId });
  }

  addGroupAssessmentId(groupAssessmentId: string): PageRequest {
    return new PageRequest({
      ...this,
      groupAssessmentId,
    });
  }

  filter(search: string): PageRequest {
    return new PageRequest({
      ...this,
      search,
    });
  }

  filterAssessments(assessments: Assessment[]): PageRequest {
    return new PageRequest({
      ...this,
      assessments: assessments.map(({ id }) => id),
    });
  }

  filterGroups(groups: Group[]): PageRequest {
    return new PageRequest({
      ...this,
      groups: groups.map(({ id }) => id),
    });
  }
  filterStatuses(statuses: ExamStatus[]): PageRequest {
    return new PageRequest({
      ...this,
      status: statuses,
    });
  }

  /**
   *
   * @returns the Http Params associated to this page
   */
  params(): Params {
    const page: any = [
      'limit',
      'offset',
      'search',
      'sort',
      'assessments',
      'groups',
      'groupId',
      'groupAssessmentId',
      'status',
    ]
      .filter((k) => this[k] !== undefined && this[k] !== '')
      .reduce((res, k) => {
        res[k] = `${this[k]}`;
        return res;
      }, {});

    const sort = [this.order, this.sort].filter((s) => !!s).join(',');
    if (sort) {
      page.sort = sort;
    }

    return page;
  }

  at(pageNum: number): PageRequest {
    return new PageRequest({
      ...this,
      offset: Math.max(this.limit * (pageNum - 1), 0),
    });
  }

  get number() {
    return Math.floor(this.offset / this.limit) + 1;
  }

  public setDefaultSort(defaultSort: string): PageRequest {
    if (this.sort) {
      return this;
    }
    return new PageRequest({
      ...this,
      sort: defaultSort,
    });
  }

  public withNolimit(): PageRequest {
    return new PageRequest({
      ...this,
      limit: undefined,
    });
  }
}

export class Page<T> extends PageRequest {
  readonly content: T[];
  readonly totalPages: number;

  constructor(
    page?: Partial<Page<T> | PageRequest>,
    content: T[] = [],
    totalPages?: number
  ) {
    super(page);
    this.content = content ?? (page as Page<T>).content ?? [];
    this.totalPages = totalPages;
  }
}
